import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserIcon,
  DevicePhoneMobileIcon,
  QrCodeIcon,
} from '@heroicons/react/24/outline';

const steps = [
  {
    Icon: UserIcon,
    titleKey: 'howItWorks.step1.title',
    defaultTitle: '1. Креирај бесплатен профил',
    textKey: 'howItWorks.step1.text',
    defaultText:
      'Внесете основни податоци, биографија и прикачете ги најомилените фотографии. Овој чекор е целосно бесплатен и засекогаш ваш.',
  },
  {
    Icon: DevicePhoneMobileIcon,
    titleKey: 'howItWorks.step2.title',
    defaultTitle: '2. Зачувајте го споменот дигитално',
    textKey: 'howItWorks.step2.text',
    defaultText:
      'Веднаш ќе добиете прекрасна, модерна веб-страница посветена на вашиот најблизок која можете да ја споделите со семејството.',
  },
  {
    Icon: QrCodeIcon,
    titleKey: 'howItWorks.step3.title',
    defaultTitle: '3. Нарачајте ја QR плочката',
    textKey: 'howItWorks.step3.text',
    defaultText:
      'Кога ќе бидете подготвени, нарачајте ја нашата премиум Inox 304 плочка за 1.990 ден. и поврзете го дигиталниот профил со нивното вечно почивалиште.',
  },
];

const HowItWorksSection = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const currentLang = location.pathname.split('/')[1] || 'mk';

  return (
    <section className="bg-white py-24 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold text-gray-800 tracking-tight">
            {t('howItWorks.title', 'Како функционира')}
          </h2>
          <div className="mt-4 mx-auto w-12 h-0.5 bg-gray-300 rounded-full" />
          <p className="mt-5 text-base text-gray-500 max-w-xl mx-auto leading-relaxed">
            {t('howItWorks.subtitle', 'Започнете денес - потполно бесплатно. Нарачајте ја плочката само кога сте подготвени.')}
          </p>
        </div>

        {/* Steps */}
        <div className="flex flex-col md:flex-row gap-6 md:gap-4 items-stretch">
          {steps.map(({ Icon, titleKey, defaultTitle, textKey, defaultText }, index) => {
            const isFree = index === 0;
            return (
              <React.Fragment key={titleKey}>
                {/* Step card */}
                <div
                  className={`flex-1 flex flex-col items-center text-center gap-5 rounded-2xl px-8 py-10 ${
                    isFree
                      ? 'bg-[#8b5a3c] text-white shadow-lg ring-2 ring-[#7a4f35] scale-[1.03]'
                      : 'bg-gray-50 text-gray-800'
                  }`}
                >
                  {/* Free badge */}
                  {isFree && (
                    <span className="inline-block bg-white text-stone-800 text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full mb-1">
                      {t('howItWorks.freeBadge', '100% Бесплатно')}
                    </span>
                  )}

                  <div
                    className={`flex items-center justify-center w-16 h-16 rounded-full shrink-0 ${
                    isFree ? 'bg-white/20' : 'bg-stone-100'
                    }`}
                  >
                    <Icon
                      className={`w-8 h-8 ${isFree ? 'text-white' : 'text-stone-600'}`}
                      aria-hidden="true"
                    />
                  </div>

                  <h3 className={`text-lg font-semibold ${isFree ? 'text-white' : 'text-gray-800'}`}>
                    {t(titleKey, defaultTitle)}
                  </h3>

                  <p className={`text-sm leading-relaxed max-w-xs ${isFree ? 'text-amber-100' : 'text-gray-500'}`}>
                    {t(textKey, defaultText)}
                  </p>

                  {isFree && (
                    <Link
                      to={`/${currentLang}/register`}
                      className="mt-2 inline-flex items-center justify-center bg-white text-[#8b5a3c] hover:bg-[#f8f6f3] font-semibold text-sm px-6 py-2.5 rounded-full transition-colors duration-200"
                    >
                      {t('howItWorks.cta', 'Започни бесплатно')}
                    </Link>
                  )}
                </div>

                {/* Connector arrow (desktop only) */}
                {index < steps.length - 1 && (
                  <div className="hidden md:flex items-center justify-center shrink-0 text-gray-300">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={1.5}
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
